import {ErrorHandler, importProvidersFrom, inject, isDevMode, provideAppInitializer} from '@angular/core';
import {bootstrapApplication} from '@angular/platform-browser';
import {provideRouter, RouteReuseStrategy} from '@angular/router';
import * as SentryAngular from '@sentry/angular';

import {routes} from './app/constants/routes';
import {RootComponent} from './app/components/root/root.component';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {HttpClient, provideHttpClient, withInterceptors} from '@angular/common/http';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {DatePipe, registerLocaleData} from '@angular/common';
import {FormlyModule} from '@ngx-formly/core';
import * as validationMessages from './app/components/formly/validation-messages';
import {IonicRouteStrategy, provideIonicAngular} from '@ionic/angular/standalone';
import {provideAnimations} from '@angular/platform-browser/animations';
import {ionicConfig} from './app/config/ionic.config';
import {provideServiceWorker} from '@angular/service-worker';
import {backendInterceptor} from './app/interceptors/backend.interceptor';
import {authInterceptor} from './app/interceptors/auth.interceptor';
import {refreshInterceptor} from './app/interceptors/refresh.interceptor';
import {
  Configuration,
  ConfigurationParameters,
  Twict4FiberWebRestSdkApiModule
} from 'twict4fiber-api-web-rest-angular-17';
import {InitializationService} from './app/services/initialization.service';
import {InputFormlyType} from './app/components/formly/input.formly-type';
import {ObjectFormlyType} from './app/components/formly/object.formly-type';
import {TextareaFormlyType} from './app/components/formly/textarea.formly-type';
import {ArrayFormlyType} from './app/components/formly/array.formly-type';
import {RadioFormlyType} from './app/components/formly/radio.formly-type';

import localeNl from '@angular/common/locales/nl';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeTr from '@angular/common/locales/tr';
import localePl from '@angular/common/locales/pl';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localeUk from '@angular/common/locales/uk';
import {defineCustomElements as loadDragResizeRotate} from '@deckdeckgo/drag-resize-rotate/dist/loader';
import {defineCustomElements as loadPwaElements} from '@ionic/pwa-elements/loader'; // https://github.com/ionic-team/pwa-elements/issues/129
import {NullFormlyType} from './app/components/formly/null.formly-type';
import {initSentry} from './util/sentry';
import {provideStorage} from '@twict4fiber/storage';
import {ngswBypassInterceptor} from './app/interceptors/ngsw-bypass.interceptor';
import {NetcomApiAdapterModule, V2ApiAdapterModule} from '@twict4fiber/data';
import {T4EMeasurementMethodFormlyType} from './components/formly/t4e-measurement-method.formly-type';

initSentry();

loadDragResizeRotate(window);

loadPwaElements(window);

registerLocaleData(localeNl, 'nl');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeTr, 'tr');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeUk, 'uk');

bootstrapApplication(RootComponent, {
  providers: [
    {provide: ErrorHandler, useValue: SentryAngular.createErrorHandler()},
    provideStorage('@twict4fiber/app'),
    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
    // {provide: LocationStrategy, useClass: HashLocationStrategy},
    {provide: DatePipe},
    provideAppInitializer(() => {
      const initializerFn = ((initializationService: InitializationService) => {
        return () => initializationService.initializeApplication();
      })(inject(InitializationService));
      return initializerFn();
    }),

    provideHttpClient(withInterceptors([
      ngswBypassInterceptor,
      authInterceptor,
      refreshInterceptor,
      backendInterceptor
    ])),

    provideAnimations(),

    provideRouter(routes),

    provideIonicAngular(ionicConfig),

    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately'
    }),


    importProvidersFrom(TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: (httpClient: HttpClient): TranslateHttpLoader => {
          return new TranslateHttpLoader(httpClient, './translations/', '.json');
        },
        deps: [HttpClient]
      }
    })),

    importProvidersFrom(V2ApiAdapterModule, NetcomApiAdapterModule),

    importProvidersFrom(FormlyModule.forRoot({
        extras: {resetFieldOnHide: true},
        validationMessages: [
          {name: 'required', message: 'This field is required'},
          {name: 'null', message: 'should be null'},
          {name: 'minLength', message: validationMessages.minlengthValidationMessage},
          {name: 'maxLength', message: validationMessages.maxlengthValidationMessage},
          {name: 'min', message: validationMessages.minValidationMessage},
          {name: 'max', message: validationMessages.maxValidationMessage},
          {name: 'multipleOf', message: validationMessages.multipleOfValidationMessage},
          {name: 'exclusiveMinimum', message: validationMessages.exclusiveMinimumValidationMessage},
          {name: 'exclusiveMaximum', message: validationMessages.exclusiveMaximumValidationMessage},
          {name: 'minItems', message: validationMessages.minItemsValidationMessage},
          {name: 'maxItems', message: validationMessages.maxItemsValidationMessage},
          {name: 'uniqueItems', message: 'should NOT have duplicate items'},
          {name: 'const', message: validationMessages.constValidationMessage}
        ],
        types: [
          {
            name: 'null',
            component: NullFormlyType
          },
          {
            name: 'object',
            component: ObjectFormlyType
          },
          {
            name: 'string',
            component: InputFormlyType
          },
          {
            name: 'input',
            component: InputFormlyType
          },
          {
            name: 'number',
            component: InputFormlyType,
            defaultOptions: {
              props: {
                type: 'number'
              }
            }
          },
          {
            name: 'integer',
            component: InputFormlyType,
            defaultOptions: {
              props: {
                type: 'number'
              }
            }
          },
          {
            name: 'radio',
            component: RadioFormlyType
          },
          {
            name: 'textarea',
            component: TextareaFormlyType
          },
          {
            name: 'array',
            component: ArrayFormlyType
          },
          {
            name: 'enum',
            extends: 'radio'
          },
          {
            name: 'select',
            extends: 'radio'
          },
          {
            name: 't4e-measurement-method',
            component: T4EMeasurementMethodFormlyType
          }
        ],
      })
    )
  ]
});
